import { useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faCamera,
  faCloudArrowUp,
} from '@fortawesome/free-solid-svg-icons';
import { Footer } from '../components/Footer';
import {
  FinalCtaSection,
  HowItWorksSection,
  SamplesSection,
} from '../components/home/homeSections';
import {
  ScoreUploadBox,
  type ScoreUploadBoxHandle,
} from '../components/ScoreUploadBox';
import { MobileAppWaitlistBanner } from '../components/MobileAppWaitlistBanner';
import { usePageMeta } from '../hooks/usePageMeta';
import { getPublicPageMeta } from '../seo/publicPageMeta';
import { useIsPhone } from '../hooks/useIsPhone';
import { useMobileOS } from '../hooks/useMobileOS';
import { useImagePageLimit } from '../hooks/useImagePageLimit';
import { useIsAuthenticated } from 'src/auth/firebaseMsalCompat';
import scanBeforeImg from '@Assets/images/scanner/scan_before.jpg';
import scanAfterImg from '@Assets/images/scanner/scan_after.jpg';
import {
  reactPlugin,
  trackButtonClick,
  trackPageView,
} from '../services/telemetry';
import {
  useAppInsightsContext,
  withAITracking,
} from '@microsoft/applicationinsights-react-js';

const SCANNER_HOW_STEPS = [
  {
    title: 'Snap or upload',
    detail: 'Photograph a page of sheet music, or drop in a PDF.',
  },
  {
    title: 'We convert the score',
    detail:
      'Our AI turns the scan into editable MusicXML. You can also view the scanned score with note names, accidental highlights and editable piano fingerings.',
  },
  {
    title: 'Download MusicXML or PDF',
    detail:
      'Export a clean MusicXML file for further edit, or an annotated PDF.',
  },
] as const;

function SheetMusicScanner() {
  const isAuthenticated = useIsAuthenticated();
  const appInsights = useAppInsightsContext();
  const mobileOS = useMobileOS();
  const { isPhone } = useIsPhone();
  const imagePageLimit = useImagePageLimit();
  const uploadRef = useRef<ScoreUploadBoxHandle>(null);
  const desktopFromMd = isPhone ? '' : 'md:block';
  const desktopFlexFromMd = isPhone ? '' : 'md:flex';

  const uploadVariant = isAuthenticated ? 'member' : 'guest';

  usePageMeta(getPublicPageMeta('/sheet-music-scanner'));

  useEffect(() => {
    trackPageView('SheetMusicScanner', '/sheet-music-scanner');
    appInsights.trackPageView({
      name: 'Sheet Music Scanner',
      isLoggedIn: isAuthenticated,
    });
  }, [isAuthenticated, appInsights]);

  const handleUploadCtaClick = () => {
    trackButtonClick('SheetMusicScanner', 'Upload My Score');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openCamera = (cta: string) => {
    void trackButtonClick('SheetMusicScanner', cta);
    uploadRef.current?.openCamera();
  };

  return (
    <>
      {mobileOS === 'ios' && (
        <MobileAppWaitlistBanner source="SheetMusicScanner" />
      )}

      <main
        className={`
          bg-gray-25 flex min-h-screen flex-col items-center p-4 font-[Outfit]
        `}
      >
        <div className={isPhone ? undefined : 'md:hidden'}>
          <div className="mx-auto w-full max-w-[640px] px-1">
            <section className="pt-2 pb-6 text-center">
              <h1
                className={`
                  mt-1.5 mb-3 text-[31px] leading-[1.14] font-extrabold
                  tracking-tight text-gray-800
                  min-[560px]:text-[40px]
                `}
              >
                Scan sheet music,
                <br />
                get <span className="text-yellow-700">MusicXML.</span>
              </h1>
              <p className="mx-auto mb-4 max-w-[36ch] text-base text-gray-500">
                Upload image or pdf. See accidental, note names and piano
                fingerings.
              </p>

              <div
                className={`
                  mb-4 grid grid-cols-[1fr_auto_1fr] items-center gap-2
                  rounded-[14px] border border-gray-200 bg-white p-2.5
                `}
              >
                <div className="rounded-lg bg-[#FFF6E8] p-1 text-center">
                  <span
                    className={`
                      mb-1 block text-[10px] font-bold tracking-wider
                      text-orange-600 uppercase
                    `}
                  >
                    Scan
                  </span>
                  <img
                    src={scanBeforeImg}
                    alt="Printed piano sheet music, photographed before conversion"
                    className={`
                      aspect-[5/6] h-auto w-full rounded-md object-contain
                    `}
                  />
                </div>
                <FontAwesomeIcon
                  icon={faArrowRight}
                  aria-hidden="true"
                  className="text-lg font-extrabold text-yellow-700"
                />
                <div className="rounded-lg bg-[#F1FAF6] p-1 text-center">
                  <span
                    className={`
                      mb-1 block text-[10px] font-bold tracking-wider
                      text-emerald-800 uppercase
                    `}
                  >
                    Clean PDF & MusicXML
                  </span>
                  <img
                    src={scanAfterImg}
                    alt="The same page after conversion, showing fingerings and note names"
                    className={`
                      aspect-[5/6] h-auto w-full rounded-md object-contain
                    `}
                  />
                </div>
              </div>

              <ScoreUploadBox
                ref={uploadRef}
                variant={uploadVariant}
                source="SheetMusicScanner"
                layout="camera"
                imagePageLimit={isAuthenticated ? imagePageLimit : 1}
              />
            </section>

            <SamplesSection
              surface="phone"
              className="mt-2 mb-1.5"
              from="/sheet-music-scanner"
            />

            <HowItWorksSection
              className="mt-10 mb-2.5"
              steps={SCANNER_HOW_STEPS}
            />

            <FinalCtaSection
              className="mt-10 mb-2.5"
              heading="Scan a page. Get MusicXML back."
              subheading="Try it on your own sheet music for free."
              label="Take a photo"
              icon={faCamera}
              onClick={() => openCamera('Take a Photo (Final CTA)')}
            />
          </div>
        </div>

        <header
          className={`
            mb-8 hidden text-center sm:mb-12 lg:mb-16
            ${desktopFromMd}
          `}
        >
          <h1
            className={`
              mt-2 mb-1 font-bold text-gray-800
              sm:mt-3 sm:mb-2 sm:text-3xl
              md:mt-4 md:mb-3 md:text-4xl
              lg:mt-6 lg:mb-4 lg:text-5xl
            `}
          >
            Scan sheet music,
            <br />
            get <span className="text-yellow-700">MusicXML.</span>
          </h1>
          <p
            className={`
              mx-auto max-w-2xl text-[14px] text-gray-700
              sm:text-xl
              md:text-2xl
              lg:text-2xl
            `}
          >
            Upload image or pdf. See accidental, note names and piano
            fingerings.
          </p>
        </header>

        <div
          className={`
            mb-8 hidden w-full max-w-6xl rounded-3xl bg-gradient-to-b
            from-yellow-50 to-white p-6 shadow-sm
            sm:p-10
            ${desktopFromMd}
          `}
        >
          <div
            className={`
              hidden items-center justify-center gap-3
              lg:gap-6
              ${desktopFlexFromMd}
            `}
          >
            <div className="shrink-0 text-center">
              <span
                className={`
                  mb-1.5 block text-[10px] font-bold tracking-wider
                  text-orange-600 uppercase
                `}
              >
                Scan
              </span>
              <img
                src={scanBeforeImg}
                alt="Printed piano sheet music before conversion"
                className={`
                  aspect-[5/6] w-full max-w-[200px] rounded-lg bg-white
                  object-contain p-2 shadow-sm
                  lg:max-w-[260px]
                `}
              />
            </div>

            <FontAwesomeIcon
              icon={faArrowRight}
              aria-hidden="true"
              className="shrink-0 text-xl text-yellow-500 lg:text-2xl"
            />

            <div className="w-full max-w-xs shrink-0 lg:max-w-sm">
              <ScoreUploadBox
                variant={uploadVariant}
                source="SheetMusicScanner"
                layout="boxed"
                imagePageLimit={isAuthenticated ? imagePageLimit : 1}
              />
            </div>

            <FontAwesomeIcon
              icon={faArrowRight}
              aria-hidden="true"
              className="shrink-0 text-xl text-yellow-500 lg:text-2xl"
            />

            <div className="shrink-0 text-center">
              <span
                className={`
                  mb-1.5 block text-[10px] font-bold tracking-wider
                  text-emerald-800 uppercase
                `}
              >
                Clean PDF & MusicXML
              </span>
              <img
                src={scanAfterImg}
                alt="The same page after conversion, showing fingerings and note names"
                className={`
                  aspect-[5/6] w-full max-w-[200px] rounded-lg bg-white
                  object-contain p-2 shadow-sm
                  lg:max-w-[260px]
                `}
              />
            </div>
          </div>
        </div>

        <SamplesSection
          surface="desktop"
          className={`
            mt-2 hidden w-full px-3
            md:max-w-3xl
            lg:max-w-4xl
            ${desktopFromMd}
          `}
          from="/sheet-music-scanner"
        />

        <HowItWorksSection
          className={`
            mt-10 hidden w-full px-3
            md:max-w-3xl
            lg:mt-14 lg:max-w-4xl
            ${desktopFromMd}
          `}
          steps={SCANNER_HOW_STEPS}
        />

        <FinalCtaSection
          className={`
            mt-8 mb-10 hidden w-full
            md:max-w-3xl
            lg:mt-12 lg:mb-14 lg:max-w-4xl
            ${desktopFromMd}
          `}
          heading="Scan a page. Get MusicXML back."
          subheading="Try it on your own sheet music for free."
          label="Upload my score"
          icon={faCloudArrowUp}
          onClick={handleUploadCtaClick}
        />
      </main>

      <Footer />
    </>
  );
}

const SheetMusicScannerWithAITracking = withAITracking(
  reactPlugin,
  SheetMusicScanner
);

export { SheetMusicScannerWithAITracking as SheetMusicScanner };
